import React, { useState, useEffect } from "react";
import { JobEditTabs } from "./JobEditTabs";
import { JobOverviewCard } from "./JobOverviewCard";
import { StickyEditBanner } from "@/components/ui/StickyEditBanner";
import { useTranslation } from "react-i18next";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { isClosedJobStatusValue } from "@/utils/jobs/status";

interface JobProfileProps {
  job: any;
  formattedDate: string;
  matchesCount: number;
  onChange: (updatedJob: any) => void;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  customFieldsConfig?: any[]; // Custom fields configuration for the organization
}

export const JobProfile: React.FC<JobProfileProps> = ({
  job,
  formattedDate,
  matchesCount,
  onChange,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  customFieldsConfig = []
}) => {
  const { t } = useTranslation();
  const [editedJob, setEditedJob] = useState(job);
  
  // State for confirmation dialog when changing to Closed
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean, value?: string }>({ open: false });
  const [closingReason, setClosingReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");

  // Update editedJob when job prop changes
  useEffect(() => {
    setEditedJob(job);
  }, [job]);

  const handleFieldChange = (field: string, value: string | string[]) => {
    // Check if the field is status and the new value is a closed job status
    if (field === 'status' && typeof value === 'string' && isClosedJobStatusValue(value) && !isClosedJobStatusValue(job.status)) {
      setConfirmDialog({ open: true, value: value as string });
      setClosingReason("");
      setCustomReason("");
      return;
    }
    
    let updatedJob = { ...editedJob };
    
    if (field === 'companyId') {
      // Check if the value is a JSON string containing both id and name
      try {
        const companyData = JSON.parse(value as string);
        if (companyData.id && companyData.name) {
          updatedJob = {
            ...updatedJob,
            companyId: companyData.id,
            company: {
              id: companyData.id,
              name: companyData.name
            },
            // Clear contact when company changes
            contactId: ''
          };
        } else {
          // Fallback to just the ID
          updatedJob = {
            ...updatedJob,
            companyId: value as string,
            company: updatedJob.company ? {
              ...updatedJob.company,
              id: value as string
            } : {
              id: value as string,
              name: ''
            },
            // Clear contact when company changes
            contactId: ''
          };
        }
      } catch {
        // If parsing fails, treat as just the ID
        updatedJob = {
          ...updatedJob,
          companyId: value as string,
          company: updatedJob.company ? {
            ...updatedJob.company,
            id: value as string
          } : {
            id: value as string,
            name: ''
          },
          // Clear contact when company changes
          contactId: ''
        };
      }
    } else if (field === 'assignedRecruiter') {
      // Handle assignedRecruiter field - convert ID to object structure
      updatedJob = {
        ...updatedJob,
        assignedRecruiter: value ? { id: value } : undefined,
        // Ensure company object is preserved
        company: updatedJob.company || job.company,
        // Ensure contact object is preserved
        contact: updatedJob.contact || job.contact
      };
    } else {
      // For other fields, preserve the company and contact objects
      updatedJob = {
        ...updatedJob,
        [field]: value,
        // Ensure company object is preserved
        company: updatedJob.company || job.company,
        // Ensure contact object is preserved
        contact: updatedJob.contact || job.contact
      };
    }
    
    setEditedJob(updatedJob);
  };

  const handleSave = async () => {
    try {
      await onChange(editedJob);
      onSave();
    } catch (error) {
      console.error('Failed to save job:', error);
      // Revert to original job data on error
      setEditedJob(job);
    }
  };

  const handleCancel = () => {
    setEditedJob(job);
    onCancel();
  };

  return (
    <div className="flex flex-col gap-6">
      {isEditing && (
        <>
          <StickyEditBanner
            isSticky={true}
            onSave={handleSave}
            onCancel={handleCancel}
          >
            {t('common.editMode')}
          </StickyEditBanner>
        </>
      )}
      
      <div className={`w-full bg-white dark:bg-gray-800 rounded-sm shadow p-6 relative ${isEditing ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-900/30' : ''}`}>
        {isEditing ? (
          <JobEditTabs
            job={editedJob}
            isEditing={isEditing}
            onChange={handleFieldChange}
            customFieldsConfig={customFieldsConfig}
          />
        ) : (
          <JobOverviewCard
            jobId={job.id}
            company={job.company}
            formattedDate={formattedDate}
            matchesCount={matchesCount}
            skills={job.skills}
            contact={job.contact}
            seniorityLevel={job.seniorityLevel}
            employmentType={job.employmentType}
            contractDuration={job.contractDuration}
            employmentClassification={job.employmentClassification}
            startDate={job.startDate}
            applicationDeadline={job.applicationDeadline}
            jobPostingDate={job.jobPostingDate}
            budget={job.budget}
            budgetFreelance={job.budgetFreelance}
            locationType={job.locationType}
            workplaceBreakdown={job.workplaceBreakdown}
            hardSkills={job.hardSkills}
            softSkills={job.softSkills}
            languageRequirements={job.languageRequirements}
            extraLang={job.extraLang}
            educationLevel={job.educationLevel}
            yearsOfExperience={job.yearsOfExperience}
            cultureTags={job.cultureTags}
            source={job.source}
            assignedRecruiter={job.assignedRecruiter}
            custom_fields={job.custom_fields}
            customFieldsConfig={customFieldsConfig}
            closing_reason={job.closing_reason}
            horizontal={true}
          />
        )}
      </div>
      
      {/* Confirmation Dialog for Closing Jobs */}
      <AlertDialog open={confirmDialog.open} onOpenChange={open => setConfirmDialog(v => ({ ...v, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("jobs.closingReason.closeTitle", "Close Job Offer?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("jobs.closingReason.closeWarning", "Changing to Closed will close the job offer and all the linked matches. Are you sure you want to continue?")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">{t("jobs.closingReason.reasonLabel", "Reason for closing")}</label>
            <Select value={closingReason} onValueChange={setClosingReason}>
              <SelectTrigger>
                <SelectValue placeholder={t("jobs.closingReason.reasonPlaceholder", "Select reason")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Expired">{t("jobs.closingReason.expired", "Expired")}</SelectItem>
                <SelectItem value="Closed by customer">{t("jobs.closingReason.closedByCustomer", "Closed by customer")}</SelectItem>
                <SelectItem value="Candidate hired">{t("jobs.closingReason.candidateHired", "Candidate hired")}</SelectItem>
                <SelectItem value="Other">{t("jobs.closingReason.other", "Other")}</SelectItem>
              </SelectContent>
            </Select>
            {closingReason === "Other" && (
              <div className="mt-2">
                <Input
                  value={customReason}
                  onChange={e => setCustomReason(e.target.value)}
                  placeholder={t("jobs.closingReason.otherPlaceholder", "Specify reason")}
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setConfirmDialog({ open: false });
              setClosingReason("");
              setCustomReason("");
            }}>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDialog.value) {
                  let reason = closingReason;
                  if (closingReason === "Other") reason = customReason;
                  
                  // Update the job with the new status and closing reason
                  const updatedJob = {
                    ...editedJob,
                    status: confirmDialog.value,
                    closing_reason: reason
                  };
                  setEditedJob(updatedJob);
                }
                setConfirmDialog({ open: false });
                setClosingReason("");
                setCustomReason("");
              }}
              className="bg-red-600 hover:bg-red-700"
              disabled={!closingReason || (closingReason === "Other" && !customReason)}
            >
              {t("common.yes", "Yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}; 